import { createClient } from '@supabase/supabase-js';
import { Database } from './database.types';

// Get environment variables with fallback values
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://uglrgwybgedlsebnxvia.supabase.co';
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InVnbHJnd3liZ2VkbHNlYm54dmlhIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Mzc5NzU5NTIsImV4cCI6MjA1MzU1MTk1Mn0.W1RhHKfiHR5PrR5orvrr7UoJZqvYLKdqHgqUsmYuJ-c';

// Create a single instance of the Supabase client with enhanced configuration
export const supabase = createClient<Database>(supabaseUrl, supabaseKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: localStorage
  },
  global: {
    headers: {
      'x-client-info': 'sollbridge-web',
    }
  },
  db: {
    schema: 'public'
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  },
  // Enhanced fetch configuration with better error handling
  fetch: (url, options = {}) => {
    const retryCount = 5;
    const baseDelay = 1000;
    const maxDelay = 10000;
    const timeout = 30000; // Increased timeout

    const fetchWithRetry = async (attempt = 0): Promise<Response> => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        const response = await fetch(url, {
          ...options,
          signal: controller.signal,
          keepalive: true
        });

        clearTimeout(timeoutId);

        if (!response.ok && attempt < retryCount) {
          const delay = Math.min(
            baseDelay * Math.pow(2, attempt),
            maxDelay
          );
          await new Promise(resolve => setTimeout(resolve, delay));
          return fetchWithRetry(attempt + 1);
        }

        return response;
      } catch (error) {
        if (attempt < retryCount) {
          const delay = Math.min(
            baseDelay * Math.pow(2, attempt),
            maxDelay
          );
          await new Promise(resolve => setTimeout(resolve, delay));
          return fetchWithRetry(attempt + 1);
        }
        throw error;
      }
    };

    return fetchWithRetry();
  }
});

// Connection state management
let isOnline = navigator.onLine;
let connectionCheckInterval: number | null = null;

// Update online status
function updateOnlineStatus(online: boolean) {
  if (isOnline !== online) {
    isOnline = online;
    if (online) {
      console.log('Connection restored, reconnecting to Supabase...');
      checkSupabaseConnection();
    } else {
      console.warn('Network connection lost');
    }
  }
}

// Start connection monitoring
export function startConnectionMonitoring() {
  window.addEventListener('online', () => updateOnlineStatus(true));
  window.addEventListener('offline', () => updateOnlineStatus(false));

  // Initial connection check
  updateOnlineStatus(navigator.onLine);

  // Periodic connection check
  if (!connectionCheckInterval) {
    connectionCheckInterval = window.setInterval(async () => {
      try {
        const response = await fetch(supabaseUrl, { 
          method: 'HEAD',
          signal: AbortSignal.timeout(5000)
        });
        updateOnlineStatus(response.ok);
      } catch {
        updateOnlineStatus(false);
      }
    }, 30000) as unknown as number;
  }

  return () => stopConnectionMonitoring();
}

// Stop connection monitoring
export function stopConnectionMonitoring() {
  window.removeEventListener('online', () => updateOnlineStatus(true));
  window.removeEventListener('offline', () => updateOnlineStatus(false));
  if (connectionCheckInterval) {
    clearInterval(connectionCheckInterval);
    connectionCheckInterval = null;
  }
}

// Helper function to handle Supabase errors with retries
export async function handleSupabaseError<T>(
  operation: () => Promise<{ data: T | null; error: any }>,
  errorContext: string,
  retries = 3
): Promise<T> {
  let lastError: any;
  
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const { data, error } = await operation();
      
      if (error) {
        if (error.message === 'Failed to fetch' && attempt < retries) {
          const delay = Math.min(1000 * Math.pow(2, attempt), 10000);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
        throw error;
      }
      
      if (!data) {
        throw new Error(`No data returned for ${errorContext}`);
      }
      
      return data;
    } catch (error) {
      lastError = error;
      
      if (error.message === 'Failed to fetch' && attempt < retries) {
        const delay = Math.min(1000 * Math.pow(2, attempt), 10000);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      
      throw lastError;
    }
  }

  throw lastError;
}

// Check Supabase connection
export async function checkSupabaseConnection(): Promise<boolean> {
  try {
    const result = await handleSupabaseError(
      () => supabase.from('profiles').select('count').limit(1),
      'connectionCheck',
      2
    );
    return !!result;
  } catch {
    return false;
  }
}